"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import {
  clearPartnerBookingOrigin,
  readPartnerBookingOrigin,
} from "@/utils/partner/PartnerContext";
import { isGlobalPath } from "@/utils/partner/partnerRoutes";

/**
 * Global partner-origin reset.
 *
 * Mounted once in the root layout. Whenever the visitor lands on a genuinely
 * global page (anything that is NOT a /partner/<slug>/ storefront and NOT a
 * branded journey route — /cart, /booking, /user/...), it clears the
 * sukuto_partner_origin cookie.
 *
 * Why: the cookie makes the shared cart/booking/account routes "follow" the
 * storefront a journey started from (branded chrome + PLATFORM_PARTNER_WEB
 * attribution). Visiting the global site is the explicit signal that the user
 * has left that storefront, so we drop the origin here — otherwise a returning
 * visitor could open their cart/account on sukuto.com and still see stale
 * partner branding. On partner storefront and journey routes this is a no-op, so
 * an in-progress white-label session is never disturbed.
 *
 * Renders nothing. The clear is a single document.cookie write (no network, no
 * layout impact, no SSR output) so there is no SEO or Core Web Vitals effect.
 * The partner storefront re-seeds the cookie on its own mount, so navigating
 * back into a storefront restores the white-label session.
 *
 * The clear only runs when an origin cookie is actually present, so the common
 * case (browsing the global marketplace with no partner session) performs no
 * cookie write at all on these high-traffic routes.
 */
const PartnerOriginReset = () => {
  const pathname = usePathname();

  useEffect(() => {
    if (isGlobalPath(pathname) && readPartnerBookingOrigin()) {
      clearPartnerBookingOrigin();
    }
  }, [pathname]);

  return null;
};

export default PartnerOriginReset;
